import {List as ImmutableList} from 'immutable';

const planOptionConfigurationGIM2 = [
  {
    label: 'Option 1: $500, $100, $100',
    amounts: {
      hosAdmission: 500,
      hosConfinement: 100,
      icuConfinement: 100,
    },
  },
  {
    label: 'Option 2: $1000, $100, $100',
    amounts: {
      hosAdmission: 1000,
      hosConfinement: 100,
      icuConfinement: 100,
    },
  },
  {
    label: 'Option 3: $1000, $200, $200',
    amounts: {
      hosAdmission: 1000,
      hosConfinement: 200,
      icuConfinement: 200,
    },
  },
  {
    label: 'Option 4: $2000, $200, $200',
    amounts: {
      hosAdmission: 2000,
      hosConfinement: 200,
      icuConfinement: 200,
    },
  },
];

const planOptionConfigurationGIM2_NJ = [
  {
    label: 'Option 1: $400, $100, $100',
    amounts: {
      hosAdmission: 400,
      hosConfinement: 100,
      icuConfinement: 100,
    },
  },
  {
    label: 'Option 2: $550, $50, $50',
    amounts: {
      hosAdmission: 550,
      hosConfinement: 50,
      icuConfinement: 50,
    },
  }
];

const planOptionConfigurationGIM2_Other = [
  {
    label: 'Option 1: $500, $100',
    amounts: {
      hosAdmission: 500,
      hosConfinement: 100,
      icuConfinement: 0,
    },
  },
  {
    label: 'Option 2: $1000, $100',
    amounts: {
      hosAdmission: 1000,
      hosConfinement: 100,
      icuConfinement: 0,
    },
  },
  {
    label: 'Option 3: $1000, $200',
    amounts: {
      hosAdmission: 1000,
      hosConfinement: 200,
      icuConfinement: 0,
    },
  },
  {
    label: 'Option 4: $2000, $200',
    amounts: {
      hosAdmission: 2000,
      hosConfinement: 200,
      icuConfinement: 0,
    },
  },
];

const planOptionConfigurationGHI3 = [
  {
    label: 'Option 1: $500, $100, $1000',
    amounts: {
      hosAdmission: 500,
      hosConfinement: 100,
      icuConfinement: 1000,
      hosAdmissionNewborn: 50,
      hosConfinementNewborn: 25,
    },
  },
  {
    label: 'Option 2: $1000, $100, $2000',
    amounts: {
      hosAdmission: 1000,
      hosConfinement: 100,
      icuConfinement: 2000,
      hosAdmissionNewborn: 50,
      hosConfinementNewborn: 25,
    },
  },
  {
    label: 'Option 3: $1000, $200, $2000',
    amounts: {
      hosAdmission: 1000,
      hosConfinement: 200,
      icuConfinement: 2000,
      hosAdmissionNewborn: 50,
      hosConfinementNewborn: 25,
    },
  },
  {
    label: 'Option 4: $2000, $200, $4000',
    amounts: {
      hosAdmission: 2000,
      hosConfinement: 200,
      icuConfinement: 4000,
      hosAdmissionNewborn: 100,
      hosConfinementNewborn: 25,
    },
  },
];

const planOptionConfigurationGHI3_NJ = [
  {
    label: 'Option 1: $400, $100, $200',
    amounts: {
      hosAdmission: 400,
      hosConfinement: 100,
      icuConfinement: 0,
      dailyICUConfinement: 200,
      dailyStepdownICUConfinement: 150,
    },
  },
  {
    label: 'Option 2: $550, $50, $100',
    amounts: {
      hosAdmission: 550,
      hosConfinement: 50,
      icuConfinement: 0,
      dailyICUConfinement: 100,
      dailyStepdownICUConfinement: 75,
    },
  },
];

const getGIM2PlanOptions = (situsStateCd?: string) => {
  if (situsStateCd === 'NJ') {
    return planOptionConfigurationGIM2_NJ;
  } else if (['MT', 'NY', 'VT', 'WA', 'WY', 'NH'].includes(situsStateCd ?? '')) {
    return planOptionConfigurationGIM2_Other;
  } else {
    return planOptionConfigurationGIM2;
  }
};

export const getPlanConfigurationOptions = (planCd: 'GIM2' | 'GHI3', situsStateCd?: string) => {
  switch (planCd) {
    case 'GIM2': {
      const gim2PlanOptions = getGIM2PlanOptions(situsStateCd);
      return gim2PlanOptions.map(option => ({
        displayValue: option.label,
        code: option.amounts,
      }));
    }

    case 'GHI3': {
      const ghi3PlanOptions = situsStateCd === 'NJ' ? planOptionConfigurationGHI3_NJ : planOptionConfigurationGHI3;
      return ghi3PlanOptions.map(option => ({displayValue: option.label, code: option.amounts}));
    }

    default:
      return [];
  }
};

const keysToIgnoreForComparison = [
  'hosAdmissionNewborn',
  'hosConfinementNewborn',
  'dailyICUConfinement',
  'icuStepdownAdmission',
  'dailyStepdownICUConfinement',
];

const getOptForComparison = (opt: ReturnType<typeof getPlanConfigurationOptions>[number]) => {
  return Object.keys(opt.code).reduce((acc, key) => {
    if (!keysToIgnoreForComparison.includes(key)) {
      acc[key] = opt.code[key];
    }
    return acc;
  }, {});
};

export const getPlanConfigurationOptionsWithRestriction = (
  planCd: 'GIM2' | 'GHI3',
  situsStateCd: string,
  selectedAmounts: ImmutableList<{hosAdmission: number; hosConfinement: number; icuConfinement: number}>,
  currentPlanIndex: number,
  mutuallyExclusiveOptionsIndices: number[]
) => {
  const options = getPlanConfigurationOptions(planCd, situsStateCd);

  const selectedBenefitAmounts = selectedAmounts?.toArray();

  // for NJ only the options with restrictions do not apply, it has different set of options
  if (situsStateCd !== 'NJ' && selectedBenefitAmounts.length > 1 && mutuallyExclusiveOptionsIndices.length > 1) {
    const selectedByIndex: Record<number, boolean> = {};

    mutuallyExclusiveOptionsIndices.forEach(idx => {
      const optCode = planCd === 'GIM2' ? options[idx]?.code : getOptForComparison(options[idx]);
      selectedByIndex[idx] = selectedBenefitAmounts?.some(
        (a, index) => index !== currentPlanIndex && Object.keys(optCode).every(key => a[key] === optCode[key])
    );
    });

    // Disable each mutually exclusive option if any other is selected
    return options.map((opt, idx) => {
      if (mutuallyExclusiveOptionsIndices.includes(idx)) {
        // Disable this option if any other mutually exclusive option is selected
        const otherIndices = mutuallyExclusiveOptionsIndices.filter(i => i !== idx);
        const shouldDisable = otherIndices.some(i => selectedByIndex[i]);
        return {...opt, disabled: shouldDisable};
      }
      return opt;
    });
  }
  return options;
};

export const getSelectedBenefitOption = (
  planCd: 'GIM2' | 'GHI3',
  amountValues: Record<string, any>,
  situsStateCd?: string
) => {
  switch (planCd) {
    case 'GIM2': {
      const gim2PlanOptions = getGIM2PlanOptions(situsStateCd);
      const selectedOption = findOption(gim2PlanOptions, amountValues);
      return selectedOption?.amounts;
    }
    case 'GHI3': {
      const ghi3PlanOptions = situsStateCd === 'NJ' ? planOptionConfigurationGHI3_NJ : planOptionConfigurationGHI3;
      const selectedOption = findOption(ghi3PlanOptions, amountValues);
      return selectedOption?.amounts;
    }
    default:
      return {};
  }
};

const findOption = (options: any[], amountValues: Record<string, any>) =>
  options.find(
    option =>
      option.amounts.hosAdmission === amountValues.hosAdmission &&
      option.amounts.hosConfinement === amountValues.hosConfinement &&
      matchingConfineAmount(amountValues.icuConfinement, option.amounts.icuConfinement)
  );

const matchingConfineAmount = (confinementAmount: number | undefined, optionAmount: number) => {
  if (confinementAmount === undefined) {
    return optionAmount === 0;
  }
  return optionAmount === confinementAmount;
};
